import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  Phone, Mail, MapPin, MessageCircle, CalendarPlus, StickyNote, MoreHorizontal, Edit, Clock, Flag,
  Pin, Upload, ChevronRight, Users, Layers, Ruler, Home, Building2, Wallet, FileText,
  PhoneCall, Image as ImageIcon, Activity as ActivityIcon, Loader2, Eye, CircleDollarSign,
  ChevronDown, X, Bell, CalendarClock, Trash2
} from 'lucide-react';
import { leadDetail360API, noteAPI, leadAPI, leadStatusAPI, followUpAPI, userAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import ProjectWorkflowDrawer from '../components/ProjectWorkflowDrawer';
import { leadNo, prioBadge } from '../lib/leadDisplay';
import { formatDateTime, formatDate } from '../lib/timeFormat';

// Every timestamp on this card renders through the ONE shared Asia/Kolkata formatter, so a
// UTC instant is converted exactly once no matter which timezone the browser sits in.
const fmt = (d, p = 'd MMM yyyy') => {
  if (!d) return '—';
  const out = p.includes('h:mm') || p.includes('HH:mm') ? formatDateTime(d) : formatDate(d);
  return out === '-' ? '—' : out;
};
const money = (n) => (n || n === 0 ? `₹ ${Number(n).toLocaleString('en-IN')}` : '—');
const titleCase = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const Card = ({ title, action, actionLabel, children, testid, className = '', icon: Icon, grow = false, scroll = false }) => (
  <section data-testid={testid} className={`flex min-h-0 flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-slate-700 dark:bg-slate-900 ${grow ? 'xl:flex-1' : ''} ${className}`}>
    {(title || action) && (
      <header className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-[15px] font-semibold text-slate-900 dark:text-slate-50">
          {Icon && <Icon className="h-4 w-4 text-slate-400" />}{title}
        </h2>
        {action && (
          <button onClick={action} data-testid={`${testid}-action`}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300">
            {actionLabel}
          </button>
        )}
      </header>
    )}
    {scroll
      ? <div className="min-h-0 flex-1 overflow-y-auto pr-1" data-testid={`${testid}-scroll`}>{children}</div>
      : children}
  </section>
);

// Requirement rows always keep their slot — a missing value shows "—" (reference behaviour)
const ReqRow = ({ icon: Icon, label, value }) => {
  const shown = value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length)
    ? '—' : (Array.isArray(value) ? value.join(' + ') : value);
  return (
    <div className="flex items-start gap-2 py-[5px]">
      {Icon && <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />}
      <span className="w-[42%] shrink-0 text-xs text-slate-500">{label}</span>
      <span className={`min-w-0 flex-1 break-words text-xs font-medium ${shown === '—' ? 'text-slate-400' : 'text-slate-800 dark:text-slate-100'}`}>{shown}</span>
    </div>
  );
};

const Row = ({ icon: Icon, label, value }) => (
  value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length) ? null : (
    <div className="flex items-start gap-2 py-[5px]">
      {Icon && <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />}
      <span className="w-[42%] shrink-0 text-xs text-slate-500">{label}</span>
      <span className="min-w-0 flex-1 break-words text-xs font-medium text-slate-800 dark:text-slate-100">
        {Array.isArray(value) ? value.join(' + ') : value}
      </span>
    </div>
  )
);

// Hot / Warm / Cold — same canonical priority values the Leads list and bulk action use
const PRIORITY_OPTIONS = [['hot', 'Hot'], ['warm', 'Warm'], ['cold', 'Cold']];

const ACT_STYLE = {
  call: ['bg-blue-100 text-blue-600', PhoneCall],
  call_logged: ['bg-blue-100 text-blue-600', PhoneCall],
  whatsapp: ['bg-emerald-100 text-emerald-600', MessageCircle],
  whatsapp_sent: ['bg-emerald-100 text-emerald-600', MessageCircle],
  email: ['bg-sky-100 text-sky-600', Mail],
  site_visit: ['bg-purple-100 text-purple-600', MapPin],
  note_added: ['bg-amber-100 text-amber-600', StickyNote],
  document_uploaded: ['bg-slate-100 text-slate-600', FileText],
  status_changed: ['bg-indigo-100 text-indigo-600', ActivityIcon],
  lead_created: ['bg-teal-100 text-teal-600', Users],
  lead_assigned: ['bg-violet-100 text-violet-600', Users],
};

export const LeadOverview = ({ leadId, tabBar, onRefresh, callButton, content = null, onOpenTab, extraActions = [], dangerAction = null }) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(false);
  const fileRef = useRef(null);
  const [statuses, setStatuses] = useState([]);
  const [savingStage, setSavingStage] = useState(false);
  const [savingPriority, setSavingPriority] = useState(false);
  const [fuOpen, setFuOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [fups, setFups] = useState([]);
  const [users, setUsers] = useState([]);
  const [moreOpen, setMoreOpen] = useState(false);
  const fuRef = useRef(null);
  const moreRef = useRef(null);
  const canEditLead = hasPermission('leads.edit');
  const canAddFollowUp = hasPermission('follow_ups.create') || hasPermission('leads.edit');

  const load = useCallback(async () => {
    try {
      const res = await leadDetail360API.get(leadId);
      setD(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load lead');
    } finally { setLoading(false); }
  }, [leadId]);

  // Follow-ups for the Overview card — reuses the existing /follow-ups list API
  const loadFups = useCallback(async () => {
    try {
      const res = await followUpAPI.getAll({ lead_id: leadId });
      setFups(res.data || []);
    } catch { setFups([]); }
  }, [leadId]);

  useEffect(() => { load(); loadFups(); }, [load, loadFups]);

  // Payment/quotation actions inside a tab body ask the shell to re-pull the aggregate
  useEffect(() => {
    const onRefresh = () => { load(); loadFups(); };
    window.addEventListener('lead360:refresh', onRefresh);
    return () => window.removeEventListener('lead360:refresh', onRefresh);
  }, [load, loadFups]);

  useEffect(() => {
    leadStatusAPI.list().then((r) => setStatuses(r.data || [])).catch(() => setStatuses([]));
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (fuRef.current && !fuRef.current.contains(e.target)) setFuOpen(false);
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const openFollowUp = async () => {
    setFuOpen((v) => !v);
    if (!users.length) {
      try { setUsers((await userAPI.getAll()).data || []); } catch { /* keep current assignee only */ }
    }
  };

  // Stage change reuses the existing lead update API (enforces leads.edit and logs old -> new)
  const changeStage = async (next) => {
    if (!next || next === d?.header?.status) return;
    setSavingStage(true);
    try {
      await leadAPI.update(leadId, { status: next });
      toast.success(`Stage moved to ${titleCase(next)}`);
      await load(); onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not change stage'); }
    finally { setSavingStage(false); }
  };

  // Priority change reuses the SAME canonical lead update API as the list/bulk priority action
  const changePriority = async (next) => {
    if (!next || next === (d?.header?.priority || '').toLowerCase()) return;
    setSavingPriority(true);
    try {
      await leadAPI.update(leadId, { priority: next });
      toast.success(`Priority set to ${titleCase(next)}`);
      await load(); onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not change priority'); }
    finally { setSavingPriority(false); }
  };

  const goTab = (tab, focusTestId) => {
    if (onOpenTab) {
      onOpenTab(tab);
      if (focusTestId) {
        setTimeout(() => document.querySelector(`[data-testid="${focusTestId}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 400);
      }
    } else {
      navigate(`/leads/${leadId}?tab=${tab}`);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { await leadAPI.uploadAttachment(leadId, file); toast.success('File uploaded'); load(); onRefresh?.(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { e.target.value = ''; }
  };

  if (loading) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;
  if (!d) return null;

  const { cards = {}, header = {} } = d;
  const req = d.requirement || {};
  const com = d.commercial;
  const proj = d.project;
  const wa = (header.whatsapp_number || '').replace(/[^\d]/g, '');

  // ---- cards, grouped by reference column; hidden cards are simply not produced (auto-reflow) ----
  const left = [];
  const center = [];
  const right = [];

  if (cards.client_requirement) left.push(
    <Card key="req" testid="card-client-requirement" title="Client Requirement" grow scroll
      action={() => navigate(`/leads/${leadId}/edit`)} actionLabel={<><Edit className="h-3 w-3" /> Edit</>}>
      <div className="divide-y divide-slate-50 dark:divide-slate-800">
        <ReqRow icon={Layers} label="Services Required" value={req.services_required} />
        <ReqRow icon={Home} label="Project Type" value={titleCase(req.project_type)} />
        <ReqRow icon={Ruler} label="Plot Size" value={req.plot_size} />
        <ReqRow icon={Ruler} label="Built-up Area" value={req.construction_area} />
        <ReqRow icon={Building2} label="Floors" value={req.num_floors} />
        <ReqRow icon={Home} label="Bedrooms / Baths" value={[req.bedrooms, req.bathrooms].filter(Boolean).join(' / ') || ''} />
        <ReqRow icon={Flag} label="Facing" value={titleCase(req.facing)} />
        <ReqRow icon={MapPin} label="Position" value={titleCase(req.plot_position)} />
        <ReqRow icon={Clock} label="Expected Start" value={req.expected_start_date ? fmt(req.expected_start_date, 'MMM yyyy') : ''} />
        <ReqRow icon={Clock} label="Expected Completion" value={req.expected_completion_date ? fmt(req.expected_completion_date, 'MMM yyyy') : ''} />
        <ReqRow icon={Wallet} label="Client Budget" value={req.budget ? `₹ ${(req.budget / 100000).toFixed(1)} Lakhs` : ''} />
        <ReqRow icon={ImageIcon} label="Preferred Style" value={req.preferred_style} />
        <ReqRow icon={MapPin} label="Location" value={header.location} />
        <ReqRow icon={MapPin} label="City" value={req.city || header.city} />
        <ReqRow icon={MapPin} label="Locality" value={req.locality} />
        <ReqRow icon={MapPin} label="Landmark" value={req.landmark} />
        <ReqRow icon={Home} label="Plot Ownership" value={titleCase(req.plot_ownership)} />
        <ReqRow icon={Wallet} label="Loan Requirement" value={typeof req.loan_required === 'boolean' ? (req.loan_required ? 'Yes' : 'No') : titleCase(req.loan_required)} />
        <ReqRow icon={FileText} label="Nagar Nigam Approval" value={titleCase(req.nagar_nigam_approval)} />
        <ReqRow icon={Flag} label="Vaastu" value={req.vaastu_requirement} />
        <ReqRow icon={FileText} label="Special Requirements" value={req.special_requirements} />
        <ReqRow icon={FileText} label="Additional Notes" value={req.additional_notes || req.notes} />
        <ReqRow icon={FileText} label="Approved Scope" value={req.approved_scope_notes} />
      </div>
    </Card>
  );

  // Lead Source no longer gets its own card — it is shown as a compact header badge
  // beside WhatsApp (reference), so Client Requirement can use the full column height.

  if (cards.files_media) left.push(
    <Card key="files" testid="card-files-media" title={`Files & Media (${d.files?.count ?? 0})`} action={() => goTab('attachments')} actionLabel="View All">
      <div className="grid grid-cols-3 gap-2">
        {(d.files?.items || []).slice(0, 5).map((f) => (
          <a key={f.id} href={leadAPI.attachmentDownloadUrl(leadId, f.id)} target="_blank" rel="noreferrer"
            data-testid={`file-${f.id}`}
            className="group flex flex-col items-center gap-1 rounded-lg border border-slate-200 p-2 transition-colors hover:border-amber-400 dark:border-slate-700">
            <span className="flex h-12 w-full items-center justify-center rounded bg-slate-100 dark:bg-slate-800">
              {(f.content_type || '').startsWith('image') ? <ImageIcon className="h-5 w-5 text-slate-400" /> : <FileText className="h-5 w-5 text-slate-400" />}
            </span>
            <span className="w-full truncate text-center text-[10px] text-slate-500">{f.filename}</span>
          </a>
        ))}
        <button onClick={() => fileRef.current?.click()} data-testid="files-upload-btn"
          className="flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 p-2 text-slate-500 transition-colors hover:border-amber-400 hover:text-amber-600 dark:border-slate-600">
          <Upload className="h-4 w-4" /><span className="text-[10px]">Upload</span>
        </button>
        <input ref={fileRef} type="file" className="hidden" onChange={upload} data-testid="files-upload-input" />
      </div>
    </Card>
  );

  if (cards.pinned_note && d.pinned_note) center.push(
    <Card key="pin" testid="card-pinned-note" className="shrink-0 border-amber-200 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-900/10">
      <div className="flex items-start gap-2">
        <Pin className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
        <div className="min-w-0 flex-1 max-h-[120px] overflow-y-auto pr-1" data-testid="pinned-note-scroll">
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-50">Pinned Note</p>
          <p className="mt-1 text-xs leading-relaxed text-slate-700 dark:text-slate-200">{d.pinned_note.content}</p>
          <p className="mt-2 text-[11px] text-slate-500">
            Added by {d.pinned_note.created_by_name || 'Unknown'} · {fmt(d.pinned_note.created_at, "d MMM yyyy, h:mm a")}
          </p>
        </div>
      </div>
    </Card>
  );

  if (cards.activity_timeline) {
    center.push(
      <Card key="timeline" testid="card-activity-timeline" title="Activity Timeline" grow scroll
        action={() => goTab('timeline')} actionLabel="All Activities">
        {(d.timeline || []).length === 0 ? <p className="py-6 text-center text-sm text-slate-500">No activity yet</p> : (
          <div data-testid="timeline-scroll-region">
          <ol className="relative space-y-4">
            {d.timeline.map((a) => {
              const [cls, Icon] = ACT_STYLE[a.activity_type] || ['bg-slate-100 text-slate-500', ActivityIcon];
              return (
                <li key={a.id} className="flex gap-3" data-testid={`timeline-item-${a.id}`}>
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${cls}`}><Icon className="h-4 w-4" /></span>
                  <div className="min-w-0 flex-1 border-b border-slate-50 pb-3 dark:border-slate-800">
                    <div className="flex items-start justify-between gap-2">
                      <p className="min-w-0 text-sm font-medium text-slate-900 dark:text-slate-100">{titleCase(a.activity_type)}</p>
                      <p className="shrink-0 text-[11px] text-slate-400">{fmt(a.created_at, 'd MMM, h:mm a')}</p>
                    </div>
                    <p className="mt-0.5 break-words text-xs text-slate-600 dark:text-slate-300">{a.description}</p>
                    {a.created_by_name && <p className="mt-1 text-[11px] text-slate-400">by {a.created_by_name}</p>}
                  </div>
                </li>
              );
            })}
          </ol>
          </div>
        )}
      </Card>
    );
  }

  // Overview slot previously occupied by the Sales Pipeline display card.
  // The stage selector in the header and all pipeline business logic stay untouched.
  if (cards.sales_pipeline) {
    const pending = fups.filter((f) => !f.completed).sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));
    const done = fups.filter((f) => f.completed).sort((a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date));
    const rows = [...pending, ...done].slice(0, 4);
    const now = Date.now();
    right.push(
      <Card key="fups" testid="card-follow-ups" title={`Follow-ups (${fups.length})`} icon={CalendarClock}
        className="shrink-0 xl:h-[300px]" scroll
        action={() => goTab('followup')} actionLabel="View All">
        {rows.length === 0 ? (
          <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3 dark:bg-slate-800">
            <p className="text-xs text-slate-500" data-testid="follow-ups-empty">No follow-ups yet</p>
            <button onClick={openFollowUp} disabled={!canAddFollowUp} data-testid="follow-ups-add-btn"
              className="rounded-lg bg-amber-500 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-amber-600 disabled:opacity-40">Schedule</button>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.map((f) => {
              const overdue = !f.completed && new Date(f.scheduled_date).getTime() < now;
              const state = f.completed ? ['Completed', 'bg-emerald-50 text-emerald-700'] : overdue ? ['Overdue', 'bg-red-50 text-red-600'] : ['Upcoming', 'bg-blue-50 text-blue-700'];
              return (
                <li key={f.id} className="py-2" data-testid={`follow-up-row-${f.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-semibold text-slate-900 dark:text-slate-50" data-testid={`follow-up-date-${f.id}`}>
                      {fmt(f.scheduled_date, "d MMM yyyy, h:mm a")}
                    </p>
                    <span className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold ${state[1]}`} data-testid={`follow-up-status-${f.id}`}>{state[0]}</span>
                  </div>
                  <p className="mt-0.5 break-words text-xs text-slate-600 dark:text-slate-300">{f.notes || f.outcome || titleCase(f.follow_up_type)}</p>
                  <div className="mt-1 flex flex-wrap gap-1.5 text-[10px]">
                    {f.follow_up_type && <span className="rounded bg-slate-100 px-2 py-0.5 text-slate-600 dark:bg-slate-800 dark:text-slate-300">{titleCase(f.follow_up_type)}</span>}
                    {f.priority && <span className="rounded bg-amber-100 px-2 py-0.5 font-medium text-amber-700" data-testid={`follow-up-priority-${f.id}`}>{titleCase(f.priority)}</span>}
                    {f.assigned_to_name && <span className="rounded bg-slate-100 px-2 py-0.5 text-slate-600 dark:bg-slate-800 dark:text-slate-300">{f.assigned_to_name}</span>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    );
  }

  if (cards.commercial_summary && com) right.push(
    <Card key="com" testid="card-commercial-summary" title="Payment Details" grow scroll className="xl:min-h-[220px]"
      action={() => goTab('project', 'payment-section')} actionLabel="View Ledger">
      {com.quotation && (
        <div className="mb-2 flex flex-wrap items-center gap-1.5 text-[11px]">
          <span className="rounded-md bg-slate-100 px-2 py-0.5 font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">{com.quotation.quotation_no || 'Quotation'}</span>
          {com.quotation.revision && <span className="rounded-md bg-slate-100 px-2 py-0.5 text-slate-600 dark:bg-slate-800">Rev. {com.quotation.revision}</span>}
          {com.quotation.status && <span className="rounded-md bg-blue-50 px-2 py-0.5 font-medium text-blue-700">{titleCase(com.quotation.status)}</span>}
        </div>
      )}
      <div className="divide-y divide-slate-50 text-xs dark:divide-slate-800">
        <div className="flex justify-between py-1.5"><span className="text-slate-500">Base Contract Value</span><span className="font-medium" data-testid="commercial-base">{money(com.base_contract_amount)}</span></div>
        <div className="flex justify-between py-1.5"><span className="text-slate-500">Approved Additional Scope</span><span className="font-medium" data-testid="commercial-scope">{com.approved_additional_scope ? `(+) ${money(com.approved_additional_scope)}` : money(0)}</span></div>
        <div className="flex justify-between py-1.5"><span className="text-slate-500">Quoted Amount</span><span className="font-medium">{money(com.quotation?.amount)}</span></div>
      </div>
      <div className="mt-2 flex items-center justify-between rounded-lg bg-amber-50 px-3 py-2 dark:bg-amber-900/20">
        <span className="text-xs font-semibold text-slate-800 dark:text-slate-100">Current Contract Value</span>
        <span className="text-sm font-bold text-slate-900 dark:text-slate-50" data-testid="commercial-total">{money(com.total_contract_amount)}</span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-emerald-50 p-2.5 dark:bg-emerald-900/20">
          <p className="text-[10px] uppercase tracking-wide text-emerald-700">Confirmed Received</p>
          <p className="text-sm font-bold text-emerald-700" data-testid="commercial-received">{money(com.amount_received)}</p>
          <p className="text-[10px] text-emerald-600">{com.received_pct}%</p>
        </div>
        <div className="rounded-lg bg-red-50 p-2.5 dark:bg-red-900/20">
          <p className="text-[10px] uppercase tracking-wide text-red-700">Pending Balance</p>
          <p className="text-sm font-bold text-red-600" data-testid="commercial-pending">{money(com.pending_amount)}</p>
          <p className="text-[10px] text-red-500">{100 - (com.received_pct || 0)}%</p>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs dark:bg-slate-800">
        <span className="text-slate-500">Payment Status</span>
        <span className="font-semibold text-slate-800 dark:text-slate-100">{titleCase(com.payment_status)}</span>
      </div>
      <div className="mt-2 flex items-end justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-400">Next Payment</p>
          <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">{com.next_payment_stage || '—'}</p>
          <p className="text-[11px] text-slate-500">Due {fmt(com.payment_due_date)}</p>
        </div>
        <button onClick={() => goTab('project', 'payment-section')} data-testid="record-payment-btn"
          className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800">
          <CircleDollarSign className="h-3.5 w-3.5" /> Record Payment
        </button>
      </div>
    </Card>
  );

  if (cards.project_details && proj) right.push(
    <Card key="proj" testid="card-project-details" title="Project Details (Linked)" className="shrink-0">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{titleCase(proj.status)}
        </span>
        <button onClick={() => setDrawer(true)} data-testid="open-workflow-btn"
          className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100">
          Open Workflow <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="divide-y divide-slate-50 dark:divide-slate-800">
        <Row label="Project Name" value={proj.project_name} />
        <Row label="Project Serial No." value={proj.project_serial || 'Not Assigned'} />
        <Row label="Project ID" value={proj.project_id} />
        <Row label={proj.project_age?.completed ? 'Project Duration' : 'Project Age'} value={proj.project_age?.label} />
        <Row label="Assigned Architect" value={proj.assigned_architect_name || 'Not Assigned'} />
        <Row label="Architect Manager" value={proj.architect_manager_name || 'Not Assigned'} />
        <Row label="Current Phase" value={proj.current_phase} />
        <Row label="Next Milestone" value={proj.next_milestone} />
        <Row label="Due Date" value={proj.due_date ? fmt(proj.due_date) : ''} />
      </div>
      <div className="mt-2">
        <div className="flex items-center justify-between text-[11px] text-slate-500"><span>Progress</span><span className="font-semibold text-slate-800 dark:text-slate-100">{proj.progress ?? 0}%</span></div>
        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${proj.progress ?? 0}%` }} />
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button onClick={() => navigate(`/architect/projects/${leadId}`)} data-testid="open-project-btn"
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200">
          <Eye className="h-3.5 w-3.5" /> Open Project
        </button>
        <button onClick={() => goTab('project', 'architect-assign-section')} data-testid="view-team-btn"
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200">
          <Users className="h-3.5 w-3.5" /> View Team
        </button>
      </div>
    </Card>
  );

  // Next Follow-up card intentionally not rendered in Overview — follow-up info lives in the
  // Follow-ups card and the right column ends with Project Details (Linked) per the reference.
  // Follow-up data, APIs and the Follow-ups tab are untouched.

  const columns = [left, center, right].filter((c) => c.length);
  const spans = { 1: ['xl:col-span-12'], 2: ['xl:col-span-6', 'xl:col-span-6'], 3: ['xl:col-span-3', 'xl:col-span-5', 'xl:col-span-4'] }[columns.length] || [];

  return (
    <div className="space-y-4" data-testid="lead-overview">
      {/* Lead header */}
      <section data-testid="lead-header" className="rounded-xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:border-slate-700 dark:bg-slate-900">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-amber-100 text-lg font-bold text-amber-700">
              {(header.customer_name || '?').charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="truncate text-2xl font-semibold text-slate-900 dark:text-slate-50" data-testid="lead-header-name">{header.customer_name}</h1>
                <button onClick={() => navigate(`/leads/${leadId}/edit`)} data-testid="lead-header-edit"><Edit className="h-4 w-4 text-slate-400 hover:text-amber-600" /></button>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                <span className="font-mono font-medium text-slate-500" data-testid="lead-header-serial">{leadNo(header)}</span>
                {canEditLead ? (
                  <span className={`relative inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${prioBadge(header.priority)[1]}`} data-testid="lead-header-temp">
                    <Flag className="h-3 w-3" />
                    <select
                      value={(header.priority || '').toLowerCase()}
                      disabled={savingPriority}
                      onChange={(e) => changePriority(e.target.value)}
                      data-testid="lead-priority-select"
                      className="cursor-pointer appearance-none bg-transparent pr-4 text-[11px] font-medium outline-none disabled:opacity-50"
                    >
                      {PRIORITY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    {savingPriority ? <Loader2 className="h-3 w-3 animate-spin" /> : <ChevronDown className="h-3 w-3" />}
                  </span>
                ) : (
                  <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${prioBadge(header.priority)[1]}`} data-testid="lead-header-temp">
                    <Flag className="h-3 w-3" />{prioBadge(header.priority)[0]}
                  </span>
                )}
                {canEditLead ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 pl-2 pr-1 py-0.5 font-semibold text-blue-700" data-testid="lead-header-stage">
                    <span>Stage:</span>
                    <select
                      value={header.status || ''}
                      disabled={savingStage}
                      onChange={(e) => changeStage(e.target.value)}
                      data-testid="lead-stage-select"
                      className="cursor-pointer appearance-none bg-transparent pr-4 text-xs font-semibold text-blue-800 outline-none disabled:opacity-50"
                    >
                      {!statuses.some((st) => (st.key || st.name) === header.status) && (
                        <option value={header.status || ''}>{titleCase(header.status)}</option>
                      )}
                      {statuses.map((st) => (
                        <option key={st.key || st.name} value={st.key || st.name}>{st.name || titleCase(st.key)}</option>
                      ))}
                    </select>
                    {savingStage ? <Loader2 className="h-3 w-3 animate-spin" /> : <ChevronDown className="h-3 w-3" />}
                  </span>
                ) : (
                  <span className="rounded-full bg-blue-50 px-2 py-0.5 font-semibold text-blue-700" data-testid="lead-header-stage">{titleCase(header.status)}</span>
                )}
              </div>
              {header.headline && <p className="mt-1.5 truncate text-xs text-slate-500">{header.headline}</p>}
            </div>
          </div>

          <div className="min-w-0 space-y-1.5 text-xs xl:w-64">
            {header.primary_mobile && (
              <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-400" />
                <span className="font-medium text-slate-800 dark:text-slate-100">{header.primary_mobile}</span>
              </p>
            )}
            {header.email && <p className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 shrink-0 text-slate-400" /><span className="truncate text-slate-700 dark:text-slate-200">{header.email}</span></p>}
            {header.location && <p className="flex items-start gap-2"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" /><span className="text-slate-700 dark:text-slate-200">{header.location}</span></p>}
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:w-[330px]">
            <div className="flex items-start gap-2 border-l border-slate-100 pl-2 dark:border-slate-800">
              <Clock className="mt-0.5 h-3.5 w-3.5 text-slate-400" />
              <div><p className="text-[10px] uppercase tracking-wide text-slate-400">Lead Age</p>
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-100" data-testid="lead-age">{header.lead_age_label || '—'}</p></div>
            </div>
            <div className="flex items-start gap-2 border-l border-slate-100 pl-2 dark:border-slate-800">
              <PhoneCall className="mt-0.5 h-3.5 w-3.5 text-slate-400" />
              <div><p className="text-[10px] uppercase tracking-wide text-slate-400">Last Contact</p>
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">{fmt(header.last_contact)}</p>
                {header.last_contact_type && <p className="text-[10px] text-slate-400">{titleCase(header.last_contact_type)}</p>}</div>
            </div>
            <div className="flex items-start gap-2 border-l border-slate-100 pl-2 dark:border-slate-800">
              <Flag className="mt-0.5 h-3.5 w-3.5 text-amber-500" />
              <div className="min-w-0"><p className="text-[10px] uppercase tracking-wide text-slate-400">Next Action</p>
                <p className="truncate text-xs font-semibold text-slate-800 dark:text-slate-100">{header.next_action || '—'}</p>
                {header.next_action_due && <p className="text-[10px] text-red-500">Due: {fmt(header.next_action_due)}</p>}</div>
            </div>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3 dark:border-slate-800">
          {callButton || (
            <a href={`tel:${header.primary_mobile || ''}`} data-testid="lead-action-call"
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-blue-700"><Phone className="h-3.5 w-3.5" /> Call</a>
          )}
          <a href={wa ? `https://wa.me/${wa}` : '#'} target="_blank" rel="noreferrer" data-testid="lead-action-whatsapp"
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-emerald-700"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
          <span data-testid="lead-client-type-badge"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 dark:border-slate-700 dark:text-slate-200">
            <Users className="h-3.5 w-3.5 text-slate-400" />{(d.lead_source?.client_type || header.client_type || 'B2C').toUpperCase()}
          </span>
          <span data-testid="lead-source-badge"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 dark:border-slate-700 dark:text-slate-200">
            <Layers className="h-3.5 w-3.5 text-slate-400" />Source: <b className="font-semibold">{d.lead_source?.lead_source || header.lead_source || '—'}</b>
          </span>
          <div className="relative" ref={fuRef}>
            <button onClick={openFollowUp} disabled={!canAddFollowUp} data-testid="lead-action-followup"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3.5 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 dark:border-slate-700 dark:text-slate-200"><CalendarPlus className="h-3.5 w-3.5" /> Add Follow-up</button>
            {fuOpen && <QuickFollowUp leadId={leadId} users={users} onClose={() => setFuOpen(false)}
              onSaved={() => { setFuOpen(false); load(); loadFups(); onRefresh?.(); }} />}
          </div>
          <button onClick={() => setNoteOpen(true)} data-testid="lead-action-note"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3.5 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200"><StickyNote className="h-3.5 w-3.5" /> Add Note</button>
          <span className="ml-auto flex items-center gap-3">
            {dangerAction && (
              <button onClick={dangerAction.onClick} data-testid={dangerAction.testId}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 px-3.5 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20">
                <Trash2 className="h-3.5 w-3.5" /> {dangerAction.label}
              </button>
            )}
            <span className="text-[11px] text-slate-500">Assigned to <b className="text-slate-700 dark:text-slate-200">{header.assigned_to_name || 'Unassigned'}</b></span>
          </span>
        </div>
      </section>

      {tabBar}

      {content || (
        /* Cards — hidden cards are never rendered, remaining columns reflow automatically */
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2 xl:grid-cols-12 xl:items-stretch" data-testid="lead-overview-grid">
          {columns.map((col, i) => (
            <div key={i} className={`flex min-w-0 flex-col gap-4 xl:h-[920px] ${spans[i] || ''} ${columns.length === 3 && i === 2 ? 'lg:col-span-2 xl:col-span-4' : ''}`}>
              {col}
            </div>
          ))}
        </div>
      )}

      {noteOpen && (
        <QuickNote leadId={leadId} onClose={() => setNoteOpen(false)}
          onSaved={() => { setNoteOpen(false); load(); onRefresh?.(); }} />
      )}

      <ProjectWorkflowDrawer leadId={leadId} open={drawer} onClose={() => setDrawer(false)} />
    </div>
  );
};

const QuickFollowUp = ({ leadId, users, onClose, onSaved }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ date: today, time: '10:00', type: 'call', assigned_to: '', reminder: '30', notes: '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!f.date || !f.time) { toast.error('Pick a date and time'); return; }
    setSaving(true);
    try {
      await followUpAPI.create({
        lead_id: leadId,
        scheduled_date: new Date(`${f.date}T${f.time}`).toISOString(),
        follow_up_type: f.type,
        notes: f.notes,
        reminder_before: Number(f.reminder) || 0,
        ...(f.assigned_to ? { assigned_to: f.assigned_to } : {}),
      });
      toast.success('Follow-up scheduled');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not schedule follow-up'); }
    finally { setSaving(false); }
  };

  const F = ({ label, children }) => (
    <label className="block"><span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-slate-400">{label}</span>{children}</label>
  );
  const cls = 'w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 outline-none focus:border-amber-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100';

  return (
    <div data-testid="quick-followup-popover"
      className="absolute left-0 z-30 mt-2 w-[320px] rounded-xl border border-slate-200 bg-white p-3 shadow-xl dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-50">Schedule Follow-up</p>
        <button onClick={onClose} data-testid="quick-followup-close"><X className="h-4 w-4 text-slate-400" /></button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <F label="Date"><input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={cls} data-testid="qfu-date" /></F>
        <F label="Time"><input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} className={cls} data-testid="qfu-time" /></F>
        <F label="Type">
          <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={cls} data-testid="qfu-type">
            {['call', 'whatsapp', 'meeting', 'site_visit', 'video_call', 'reminder', 'quotation_followup'].map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
          </select>
        </F>
        <F label="Reminder">
          <select value={f.reminder} onChange={(e) => setF({ ...f, reminder: e.target.value })} className={cls} data-testid="qfu-reminder">
            {[['0', 'None'], ['15', '15 min before'], ['30', '30 min before'], ['60', '1 hour before'], ['1440', '1 day before']]
              .map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </F>
      </div>
      <div className="mt-2">
        <F label="Assigned To">
          <select value={f.assigned_to} onChange={(e) => setF({ ...f, assigned_to: e.target.value })} className={cls} data-testid="qfu-assignee">
            <option value="">Keep current owner</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </F>
      </div>
      <div className="mt-2">
        <F label="Agenda / Note">
          <textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
            placeholder="What is this follow-up about?" className={cls} data-testid="qfu-notes" />
        </F>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button onClick={onClose} data-testid="qfu-cancel" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 dark:border-slate-700 dark:text-slate-300">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="qfu-submit"
          className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600 disabled:opacity-60">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />} Schedule Follow-up
        </button>
      </div>
    </div>
  );
};

// Global Add Note — works from every Lead 360 tab, reuses the existing note API
const QuickNote = ({ leadId, onClose, onSaved }) => {
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    boxRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = async () => {
    if (!content.trim()) { toast.error('Write a note first'); return; }
    setSaving(true);
    try {
      await noteAPI.create({ lead_id: leadId, content: content.trim(), attachments: [] });
      toast.success('Note added');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add note'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 p-4 pt-24" data-testid="global-note-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div data-testid="global-note-modal"
        className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-4 shadow-xl dark:border-slate-700 dark:bg-slate-900">
        <div className="mb-3 flex items-center justify-between">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 dark:text-slate-50">
            <StickyNote className="h-4 w-4 text-amber-500" /> Add Note
          </p>
          <button onClick={onClose} data-testid="global-note-close"><X className="h-4 w-4 text-slate-400" /></button>
        </div>
        <textarea ref={boxRef} rows={4} value={content} onChange={(e) => setContent(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save(); }}
          placeholder="Write a note about this lead..." data-testid="global-note-input"
          className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:border-amber-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100" />
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={onClose} data-testid="global-note-cancel"
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 dark:border-slate-700 dark:text-slate-300">Cancel</button>
          <button onClick={save} disabled={saving || !content.trim()} data-testid="global-note-submit"
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-40">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <StickyNote className="h-3.5 w-3.5" />} Save Note
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadOverview;
